import { getDivergingColors, getSequentialColors, getThemeCategoricalColors } from "../../../utils/colors";
import type { ColorSpec, DivergingScheme, SequentialScheme } from "./types";

/**
 * The color ramps a layer can be shaded by, as an enumerable catalog.
 *
 * The palettes and the `ColorSpec` shapes already exist; what this adds is a list
 * a picker can render — id, label, preview stops — paired with a builder that
 * produces the `ColorSpec` the layer system consumes. UI enumerates
 * `COLOR_RAMPS`, and whatever the user chooses goes straight onto a layer's
 * `style.color`, so there is no second representation to keep in sync.
 *
 * Note the underlying constraint: palette-driven color needs the feature values
 * client-side, so it applies to GeoJSON layers whose `data` is an object rather
 * than a URL.
 */

export type ColorRampKind = "sequential" | "diverging" | "categorical";

/** What a ramp needs to know about the data it's shading. */
export interface ColorRampInput {
  /** Feature property to read the value from. */
  by: string;
  /** Value range. Defaults to `"auto"`, computed from the data. */
  scale?: { min: number; max: number } | "auto";
  /**
   * Diverging only: the midpoint the two hues meet at. Defaults to `0`, which is
   * the meaningful center for anomalies and deltas; pass `"median"`/`"mean"` for
   * distributions with no natural zero.
   */
  center?: number | "median" | "mean";
}

export interface ColorRampOption {
  /** Stable id, safe to persist in saved views. */
  id: string;
  label: string;
  /** Groups ramps in a picker and says how values map onto colors. */
  kind: ColorRampKind;
  /** One-line description of the data shape this suits. */
  hint: string;
  /**
   * Stops for a preview swatch, low to high.
   *
   * A function, not an array: the stops resolve from CSS custom properties, so a
   * captured array would freeze whichever theme was active at import time —
   * before a `ColorModeProvider` has even mounted. Results are memoized in the
   * shared color cache, so a theme switch needs `clearColorCache()`, the same as
   * every other consumer of resolved colors.
   */
  stops: () => string[];
  /** The `ColorSpec` to put on a layer's `style.color`. */
  toColorSpec: (input: ColorRampInput) => ColorSpec;
}

const sequential = (id: SequentialScheme, label: string): ColorRampOption => ({
  id,
  label,
  kind: "sequential",
  hint: "Low → High",
  stops: () => getSequentialColors(id),
  toColorSpec: ({ by, scale = "auto" }) => ({ by, palette: "sequential", scheme: id, scale }),
});

const diverging = (id: DivergingScheme, label: string): ColorRampOption => ({
  id,
  label,
  kind: "diverging",
  hint: "Low → Mid → High",
  stops: () => getDivergingColors(id),
  toColorSpec: ({ by, scale = "auto", center = 0 }) => ({ by, palette: "diverging", scheme: id, center, scale }),
});

/** Ramps for values that run low to high — load, capacity, density. */
export const SEQUENTIAL_RAMPS: ColorRampOption[] = [
  sequential("viridis", "Viridis"),
  sequential("magma", "Magma"),
  sequential("cerulean", "Cerulean"),
  sequential("forest", "Forest"),
  sequential("rose", "Rose"),
];

/** Ramps for values with a meaningful middle — anomalies, deltas, over/under. */
export const DIVERGING_RAMPS: ColorRampOption[] = [
  diverging("redYellowBlue", "Red → Blue"),
  diverging("orangeYellowSeafoam", "Orange → Seafoam"),
  diverging("redBlue", "Red / Blue"),
];

/** For values that are labels rather than magnitudes — state, type, owner. */
export const CATEGORICAL_RAMP: ColorRampOption = {
  id: "categorical",
  label: "Categorical",
  kind: "categorical",
  hint: "Distinct per value",
  stops: () => getThemeCategoricalColors(),
  toColorSpec: ({ by }) => ({ by, palette: "categorical", scale: "auto" }),
};

/** Every ramp, in the order a picker should offer them. */
export const COLOR_RAMPS: ColorRampOption[] = [...SEQUENTIAL_RAMPS, ...DIVERGING_RAMPS, CATEGORICAL_RAMP];

/** Look up a ramp by the id stored on a layer or in a saved view. */
export function getColorRamp(id: string): ColorRampOption | undefined {
  return COLOR_RAMPS.find((ramp) => ramp.id === id);
}

/** Ramps of one kind, for a picker that groups them by section. */
export function getColorRampsByKind(kind: ColorRampKind): ColorRampOption[] {
  return COLOR_RAMPS.filter((ramp) => ramp.kind === kind);
}
